import { useState, useEffect, useMemo, useCallback } from "react";
import {
  UnifiedHeader,
  toPhaseData,
  type AmbientState,
} from "./UnifiedHeader.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { api } from "../api/client.js";
import type { DashboardResponse } from "../api/types.js";

// ─── Derive ambient state from dashboard data ──────────────────
// Exported so the v4 shell's ambient ScreenHeader (PWA V2 slice 4) reuses the
// exact same calm/attention/alert derivation as the legacy UnifiedHeader.
export function deriveState(data: DashboardResponse | null): {
  state: AmbientState;
  pulse: string;
} {
  if (!data) {
    return { state: "calm", pulse: "connecting..." };
  }

  const attFeed = data.attentionFeed ?? [];
  const hasP1 = attFeed.some(
    (a) => a.severity === "P1" || a.urgency === "urgent",
  );
  const hasRed = attFeed.some(
    (a) =>
      a.kind === "red" ||
      a.title.toLowerCase().includes("ci red") ||
      a.severity === "critical",
  );

  if (hasRed || hasP1) {
    return {
      state: "alert",
      pulse: hasRed
        ? "CI red cascade · auto-deploys paused"
        : "P1 item open · needs human triage",
    };
  }

  const atRisk = attFeed.filter(
    (a) => a.severity === "warning" || a.urgency === "soon",
  ).length;
  if (atRisk > 0) {
    return {
      state: "attention",
      pulse: `${atRisk} item${atRisk > 1 ? "s" : ""} need a look`,
    };
  }

  return { state: "calm", pulse: "all systems nominal · planner armed" };
}

// ─── Shell ─────────────────────────────────────────────────────
export function Shell({
  children,
  nav,
  onSettingsClick,
}: {
  children: React.ReactNode;
  nav?: React.ReactNode;
  onSettingsClick?: () => void;
}) {
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);

  const loadDashboard = useCallback(async () => {
    try {
      const d = await api.dashboard();
      setDashboard(d);
    } catch {
      // silently retry on next interval
    }
  }, []);

  useEffect(() => {
    void loadDashboard();
    const interval = setInterval(() => void loadDashboard(), 30_000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const { state, pulse } = useMemo(
    () => deriveState(dashboard),
    [dashboard],
  );

  const phases = useMemo(
    () => toPhaseData(dashboard?.phase?.phases ?? []),
    [dashboard],
  );

  const northStar = dashboard?.phase?.northStar;

  const [killed, setKilled] = useState(false);
  useEffect(() => {
    setKilled(state === "alert");
  }, [state]);

  return (
    <div className="phone">
      <ErrorBoundary name="Header">
        <UnifiedHeader
          state={state}
          pulse={pulse}
          phases={phases}
          northStar={northStar}
          killable={state === "alert"}
          killed={killed}
          onKillToggle={() => setKilled((k) => !k)}
          onSettingsClick={onSettingsClick}
        />
      </ErrorBoundary>
      <div className="scroll">{children}</div>
      {nav}
    </div>
  );
}
